import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Send, User, Mail, Phone, Clock, Tag,
  CheckCircle, XCircle, AlertCircle, UserPlus, Save, RefreshCw
} from 'lucide-react';
import adminService from '../../services/admin/adminService';
import {
  SupportTicket,
  SupportTicketMessage,
  SupportTicketStatus,
  UpdateSupportTicketStatusRequest,
  AddSupportTicketMessageRequest,
  AssignSupportTicketRequest
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const SupportTicketDetails: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
  
  // Form states
  const [newMessage, setNewMessage] = useState('');
  const [isInternalMessage, setIsInternalMessage] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState<SupportTicketStatus>('open');
  const [statusNotes, setStatusNotes] = useState('');
  const [assignToAdminId, setAssignToAdminId] = useState('');

  useEffect(() => {
    if (ticketId) {
      loadTicketDetails();
    }
  }, [ticketId]);

  const loadTicketDetails = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const data = await adminService.getSupportTicketById(Number(ticketId));
      setTicket(data.ticket);
      setMessages(data.messages);
      setStatusUpdate(data.ticket.status);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load ticket details');
      navigate('/admin/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketId || !newMessage.trim()) return;

    setSaving(true);
    try {
      const data: AddSupportTicketMessageRequest = {
        ticket_id: Number(ticketId),
        message: newMessage,
        is_internal: isInternalMessage
      };
      await adminService.addSupportTicketMessage(data);
      toast.success('Message sent successfully');
      setNewMessage('');
      setIsInternalMessage(false);
      await loadTicketDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketId) return;

    setSaving(true);
    try {
      const data: UpdateSupportTicketStatusRequest = {
        ticket_id: Number(ticketId),
        status: statusUpdate,
        notes: statusNotes || undefined
      };
      await adminService.updateSupportTicketStatus(data);
      toast.success('Ticket status updated successfully');
      setStatusNotes('');
      await loadTicketDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTicket = async () => {
    if (!ticketId || !assignToAdminId) {
      toast.error('Please enter admin ID');
      return;
    }

    setSaving(true);
    try {
      const data: AssignSupportTicketRequest = {
        ticket_id: Number(ticketId),
        assigned_to_admin_id: Number(assignToAdminId)
      };
      await adminService.assignSupportTicket(data);
      toast.success('Ticket assigned successfully');
      setAssignToAdminId('');
      await loadTicketDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign ticket');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return badges[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-secondary-600">Ticket not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/support')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-secondary-900">{ticket.ticket_number}</h1>
          <p className="text-secondary-600 mt-1">{ticket.subject}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4">Ticket Details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-600">Description</label>
                <p className="mt-1 text-secondary-900">{ticket.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-600">Status</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(ticket.status)}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Priority</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Type</label>
                  <p className="mt-1 text-secondary-900 capitalize">{ticket.ticket_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Created</label>
                  <p className="mt-1 text-secondary-900">
                    {new Date(ticket.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4">Messages</h2>
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {messages.length === 0 ? (
                <p className="text-secondary-600 text-center py-8">No messages yet</p>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.message_id}
                    className={`p-4 rounded-lg ${
                      message.sender_type === 'admin'
                        ? 'bg-primary-50 border border-primary-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-secondary-900">
                          {message.admin_name || message.user_name || message.business_name || 'Unknown'}
                        </span>
                        {message.is_internal === 1 && (
                          <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                            Internal
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-secondary-500">
                        {new Date(message.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-secondary-900">{message.message}</p>
                  </div>
                ))
              )}
            </div>

            {/* Send Message Form */}
            <form onSubmit={handleSendMessage} className="mt-6 space-y-4">
              <div>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isInternalMessage}
                    onChange={(e) => setIsInternalMessage(e.target.checked)}
                    className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700">Internal note (not visible to customer)</span>
                </label>
                <button
                  type="submit"
                  disabled={saving || !newMessage.trim()}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Send size={18} />
                  {saving ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Creator Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Creator Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User size={18} className="text-secondary-500" />
                <span className="text-secondary-900">{ticket.creator_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-secondary-500" />
                <span className="text-secondary-900">{ticket.creator_email}</span>
              </div>
              {ticket.creator_phone && (
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-secondary-500" />
                  <span className="text-secondary-900">{ticket.creator_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Tag size={18} className="text-secondary-500" />
                <span className="text-secondary-900 capitalize">{ticket.created_by_type}</span>
              </div>
            </div>
          </div>

          {/* Assigned To */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Assignment</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-600">Currently Assigned To</label>
                <p className="mt-1 text-secondary-900">
                  {ticket.assigned_admin_name || 'Unassigned'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-secondary-600 mb-2 block">
                  Assign to Admin ID
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={assignToAdminId}
                    onChange={(e) => setAssignToAdminId(e.target.value)}
                    placeholder="Admin ID"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    onClick={handleAssignTicket}
                    disabled={saving || !assignToAdminId}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <UserPlus size={18} />
                    Assign
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Update Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Update Status</h3>
            <form onSubmit={handleUpdateStatus} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-600 mb-2 block">Status</label>
                <select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value as SupportTicketStatus)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-secondary-600 mb-2 block">Notes (Optional)</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add notes about this status change..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save size={18} />
                {saving ? 'Updating...' : 'Update Status'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetails;

